import { CampaignDetailsFields } from "Components/campaigns/CampaignDetails";
import apiConfig from "constants/apiConfig";
import { Campaign, CampaignDetail } from "types/campaign";
import { CTA, Milestone } from "types/campaign/collaborations";
import { CampaignRequirementResponse, RequirementDetails } from "types/campaign/requirement";
import { CampaignActivity } from "types/campaign/activity";
import { CampaignsResponse } from "types/campaign";
import { CampaignInsightsData } from "types/campaign/insights";
import { CampaignPerformanceData } from "types/campaign/performance";
import { CampaignPost } from "types/campaign/posts";
import axiosClient from "utils/axiosClient";
import { DiscoveryCreatorSort, RecommendedCreatorsResponse } from "types/creator-discovery";

const withCampaignId = (url: string, campaignId: string) => url.replace('<campaignId>', campaignId);

export const fetchCampaigns = async (payload: { page: number, status: string, q?: string, agency_id?: string, country?: string }): Promise<CampaignsResponse> => {
    const response = await axiosClient.get(apiConfig.FETCH_CAMPAIGNS.url, {
        params: {
            page: payload.page,
            status: payload.status,
            ...(payload.q ? { q: payload.q } : {}),
            ...(payload.agency_id ? { agency_id: payload.agency_id } : {}),
            ...(payload.country ? { country: payload.country } : {}),
        },
    });
    return response.data;
};

export const fetchCampaignsCount = async (payload?: { agency_id?: string, country?: string }) => {
    const response = await axiosClient.get(apiConfig.FETCH_CAMPAIGNS_COUNT.url, {
        params: {
            ...(payload?.agency_id ? { agency_id: payload.agency_id } : {}),
            ...(payload?.country ? { country: payload.country } : {}),
        },
    });
    return response.data.totals;
};

export const fetchCampaignActivity = async (): Promise<{ items: CampaignActivity[] }> => {
    const response = await axiosClient.get(apiConfig.FETCH_CAMPAIGNS_ACTIVITY.url);
    return response.data;
};

export const fetchCampaignsActivity = async (payload: Record<string, unknown>) => {
    const response = await axiosClient.get(apiConfig.FETCH_CAMPAIGNS_ACTIVITY.url, { params: { ...payload } });
    return response.data;
};

export const fetchCampaign = async (campaignId: string): Promise<CampaignDetail> => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN.url, campaignId));
    return response.data;
};

export const createCampaign = async (payload: CampaignDetailsFields) => {
    const response = await axiosClient.post(apiConfig.CREATE_CAMPAIGN.url, { ...payload });
    return response.data;
};

export const fetchCampaignDetails = async (campaignId: string) => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_DETAILS.url, campaignId));
    return response.data;
};

export const fetchCampaignCreateData = async () => {
    const response = await axiosClient.get(apiConfig.FETCH_CAMPAIGN_CREATE_DATA.url);
    return response.data;
};

export const fetchCampaignScreeningQuestions = async (campaignId: string) => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_SCREENING_QUESTIONS.url, campaignId));
    return response.data;
};

export const createCampaignScreeningQuestion = async (campaignId: string, payload: unknown) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.CREATE_CAMPAIGN_SCREENING_QUESTION.url, campaignId), payload);
    return response.data;
};

export const updateCampaignScreeningQuestion = async (campaignId: string, questionId: string, payload: unknown) => {
    const response = await axiosClient.put(
        withCampaignId(apiConfig.UPDATE_CAMPAIGN_SCREENING_QUESTION.url, campaignId).replace('<questionId>', questionId),
        payload
    );
    return response.data;
};

export const deleteCampaignScreeningQuestion = async (campaignId: string, questionId: string) => {
    const response = await axiosClient.delete(
        withCampaignId(apiConfig.DELETE_CAMPAIGN_SCREENING_QUESTION.url, campaignId).replace('<questionId>', questionId)
    );
    return response.data;
};

export const updateCampaignDetails = async (campaignId: string, payload: CampaignDetailsFields) => {
    const response = await axiosClient.put(withCampaignId(apiConfig.UPDATE_CAMPAIGN_DETAILS.url, campaignId), { ...payload });
    return response.data;
};

export const fetchCampaignRequirement = async (campaignId: string): Promise<CampaignRequirementResponse> => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_REQUIREMENT.url, campaignId));
    return response.data;
};

export const fetchCities = async (q: string = '', country?: string): Promise<string[]> => {
    const response = await axiosClient.get(apiConfig.FETCH_CITIES.url, {
        params: {
            q,
            ...(country ? { country } : {}),
        },
    });
    return response.data.results as string[];
};

export const fetchTopics = async (q: string = ''): Promise<string[]> => {
    const response = await axiosClient.get(apiConfig.FETCH_TOPICS.url, { params: { q } });
    return response.data.results as string[];
};

export const updateCampaignRequirement = async (campaignId: string, payload: RequirementDetails) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.FETCH_CAMPAIGN_REQUIREMENT.url, campaignId), payload);
    return response.data;
};

export const fetchCampaignCreatives = async (campaignId: string) => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_CREATIVES.url, campaignId));
    return response.data;
};

export const fetchCampaignGallery = async (campaignId: string) => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_GALLERY.url, campaignId));
    return response.data;
};

export const addCampaignGalleryImage = async (campaignId: string, payload: FormData) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.ADD_CAMPAIGN_GALLERY_IMAGE.url, campaignId), payload, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
};

export const deleteCampaignGalleryImage = async (campaignId: string, fileId: string) => {
    const response = await axiosClient.delete(
        withCampaignId(apiConfig.DELETE_CAMPAIGN_GALLERY_IMAGE.url, campaignId).replace('<fileId>', fileId)
    );
    return response.data;
};

export const updateCampaignCreatives = async (campaignId: string, payload: FormData) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.UPDATE_CAMPAIGN_CREATIVES.url, campaignId), payload, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
};

export const requestCampaignApproval = async (campaignId: string) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.REQUEST_CAMPAIGN_APPROVAL.url, campaignId));
    return response.data;
};

export const markCampaignComplete = async (campaignId: string) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.MARK_CAMPAIGN_COMPLETED.url, campaignId));
    return response.data;
};

export const fetchCampaignMilestones = async (campaignId: string) => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_MILESTONES.url, campaignId));
    return response.data;
};

export const updateCampaignMilestones = async (campaignId: string, milestones: Milestone[]) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.UPDATE_CAMPAIGN_MILESTONES.url, campaignId), { milestones });
    return response.data;
};

export const updateCampaignMilestone = async (campaignId: string, milestoneId: string, milestone: Milestone) => {
    const response = await axiosClient.put(
        withCampaignId(apiConfig.UPDATE_CAMPAIGN_MILESTONE.url, campaignId).replace('<milestoneId>', milestoneId),
        milestone
    );
    return response.data;
};

export const deleteCampaignMilestone = async (campaignId: string, milestoneId: string) => {
    const response = await axiosClient.delete(
        withCampaignId(apiConfig.UPDATE_CAMPAIGN_MILESTONE.url, campaignId).replace('<milestoneId>', milestoneId)
    );
    return response.data;
};

export const createCampaignMilestone = async (campaignId: string, payload: Record<string, unknown>) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.CREATE_CAMPAIGN_MILESTONE.url, campaignId), payload);
    return response.data;
};

export const fetchCampaignGigs = async (campaignId: string) => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_GIGS.url, campaignId));
    return response.data.gigs;
};

export const updateCampaignGig = async (campaignId: string, gigId: number, payload: Record<string, unknown>) => {
    const response = await axiosClient.put(
        withCampaignId(apiConfig.UPDATE_CAMPAIGN_GIG.url, campaignId).replace('<gigId>', String(gigId)),
        payload
    );
    return response.data;
};

export const createCampaignGig = async (campaignId: string, payload: Record<string, unknown>) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.CREATE_CAMPAIGN_GIG.url, campaignId), payload);
    return response.data;
};

export const updateMilestoneGigs = async (campaignId: string, milestoneId: string | number, ids: number[]) => {
    const response = await axiosClient.post(
        withCampaignId(apiConfig.UPDATE_MILESTONE_GIGS.url, campaignId).replace('<milestoneId>', String(milestoneId)),
        { ids }
    );
    return response.data;
};

export const updateMilestoneCTAs = async (campaignId: string, milestoneId: string | number, cta: CTA[]) => {
    const response = await axiosClient.post(
        withCampaignId(apiConfig.UPDATE_MILESTONE_CTAS.url, campaignId).replace('<milestoneId>', String(milestoneId)),
        { cta }
    );
    return response.data;
};

export const createAddress = async (payload: Record<string, unknown>) => {
    const response = await axiosClient.post(apiConfig.CREATE_ADDRESS.url, payload);
    return response.data;
};

export const fetchCampaignInsights = async (campaignId: string): Promise<CampaignInsightsData> => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_INSIGHTS.url, campaignId));
    return response.data;
};

export const fetchCampaignPerformance = async (campaignId: string, payload: Record<string, unknown> = {}): Promise<CampaignPerformanceData> => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_PERFORMANCE.url, campaignId), { params: payload });
    return response.data;
};

export const fetchCampaignContent = async (campaignId: string, payload: Record<string, unknown>): Promise<{ count: number, items: CampaignPost[] }> => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_CAMPAIGN_CONTENT.url, campaignId), { params: payload });
    return response.data;
};

export const searchCampaigns = async (q: string = '', filters?: Record<string, unknown>): Promise<any[]> => {
    const response = await axiosClient.get(apiConfig.SEARCH_CAMPAIGNS.url, {
        params: { q, ...(filters ? { ...filters } : {}) },
    });
    return response.data;
};

export const inviteCreatorsToCampaign = async (campaignId: string, socialAccountIds: string[]) => {
    const response = await axiosClient.post(
        withCampaignId(apiConfig.INVITE_CREATORS_TO_CAMPAIGN.url, campaignId),
        { social_accounts: socialAccountIds }
    );
    return response.data;
};


export const fetchRecommendedCreators = async (
    campaignId: string,
    page = 1,
    sort: DiscoveryCreatorSort = 'rank',
    limit?: number
): Promise<RecommendedCreatorsResponse> => {
    const response = await axiosClient.get(withCampaignId(apiConfig.FETCH_RECOMMENDED_CREATORS.url, campaignId), {
        params: {
            page,
            sort,
            ...(limit !== undefined ? { limit } : {}),
        },
    });
    return response.data;
};

export const generateCreatorRecommendation = async (campaignId: string) => {
    const response = await axiosClient.post(withCampaignId(apiConfig.GENERATE_CREATOR_RECOMMENDATION.url, campaignId));
    return response.data;
};

export const fetchCampaignInvitedCreators = async (
    campaignId: string,
    filters: Record<string, unknown> = {}
) => {
    const response = await axiosClient.get(
        withCampaignId(apiConfig.FETCH_CAMPAIGN_INVITED_CREATORS.url, campaignId),
        { params: filters }
    );
    return response.data;
};